<template>
  <div class="main-collection critical-component-clear">
    <div class="main-collection__content">
      <!-- Collection Filters -->

      <div class="container main-collection__product-count--mobile">
        <p
          class="text-body-xs-regular"
          v-text="$string('filters.total_product_count', {
            replace: { count: productCount },
            pluralise: productCount,
          })"
        />
      </div>

      <filter-search-filters
        :active-filters="activeFilters"
        :filters="localFilters"
        :filter-shortcuts="filterShortcuts"
        :mobile-column-count="mobileColumnCount"
        :product-count="productCount"
        :sorting="localSorting"
        @filter-change="handleFilterChange"
        @filter-clear-all="handleClearAll"
        @filter-price-change="handleFilterPriceChange"
        @mobile-column-count-change="handleMobileColumnCountChange"
        @sort-change="handleSortChange"
      />

      <!-- Product Grid -->
      <filter-search-product-grid
        ref="productGrid"
        :active-filters="activeFilters"
        :loading="state === 'loading'"
        :mobile-column-count="mobileColumnCount"
        :pagination="pagination"
        :products="localProducts"
        :total-product-count="productCount"
        :in-grid-banner="settings.inGridBanner"
        @load-more="handleLoadMore"
        @load-previous="handleLoadPrevious"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Main collection (main-collection)
 * -----------------------------------------------------------------------------
 * Component to handle Collection filter and searching with Shopify Search and
 * Discovery.
 *
 * @param {String} liquid.sectionId - Section's Liquid ID.
 * @param {Object} collection - Canvas formatted Collection data.
 *
 */

import { queryJSONTemplate } from '~/helpers/query-json'
import { unenscapeHTMLEntities } from '~/helpers/general'
import QueryCache from '~/helpers/query-caching'

import FilterSearchProductGrid from '~async/filter-search/filter-search-product-grid'
import FilterSearchFilters from '~async/filter-search/filter-search-filters'

const queryCache = QueryCache({
  key: 'cnvs-collection-data',
})

export default {
  name: 'MainCollection',

  components: {
    FilterSearchFilters,
    FilterSearchProductGrid,
  },

  props: {
    collection: {
      type: Object,
      default: () => ({}),
    },
    filterShortcuts: {
      type: Array,
      default: () => [],
    },
    sorting: {
      type: Object,
      default: () => ({}),
    },
    settings: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      activeFilters: [],
      localFilters: this.collection.filters || [],
      localProducts: this.collection.products || [],
      localSorting: this.sorting,
      mobileColumnCount: Number(this.$variable('mobileProductGridColumns')),
      pagination: {
        startPage: 1,
        page: 1,
        hasNextPage: false,
        hasPreviousPage: false,
      },
      productCount: this.collection.productsCount,
      state: 'ready',
    }
  },

  computed: {

    /**
     * Compute an array of all the filter param names.
     * - Used to remove filter params when needed.
     */
    filterParamNames() {
      const paramNames = this.localFilters.map((filter) => {
        if (filter.type === 'price_range') {
          return [filter.minValue.paramName, filter.maxValue.paramName]
        }

        return filter.paramName
      }).flat()

      return paramNames
    },
  },

  mounted() {
    this.setInitialPagination(this.collection.pageInfo)
    this.setInitialActiveFilters(this.collection.filters)
    this.checkForCachedData()
  },

  methods: {

    /**
     * Query new collection data.
     * @param {Object} options - options for the query
     * @param {Boolean} options.loadPrevious - whether the request is for
     * loading the previous page
     * @param {Boolean} options.resetPaging - whether to reset all paging data
     */
    async queryCollection({
      loadPrevious = false,
      resetPaging = false,
    } = {}) {
      this.state = 'loading'

      if (resetPaging) {
        this.pagination.startPage = 1
        this.pagination.page = 1
        this.pagination.hasPreviousPage = false
      }

      const url = this.buildQueryParameters({
        loadPrevious,
      })

      const collectionData = await this.getCollectionWithCaching(url)

      /**
       * Update local product data.
       * - If we're resetting paging, completely replace the local products
       *   array.
       * - If it's a load previous request, then we prepend the products.
       * - Otherwise, append the new products to the local products.
       */
      if (resetPaging) {
        this.localProducts = collectionData.products
      } else if (loadPrevious) {
        this.localProducts = [
          ...collectionData.products,
          ...this.localProducts,
        ]
      } else {
        this.localProducts = [
          ...this.localProducts,
          ...collectionData.products,
        ]
      }

      this.localFilters = collectionData.filters
      this.productCount = collectionData.productsCount
      this.updateLocalPagination(collectionData.pageInfo, loadPrevious)

      this.saveDataToCache({
        collectionData,
        products: this.localProducts,
        url,
      })

      if (!resetPaging && !loadPrevious) {
        this.updateFocus()
      }
      // Update URL history state
      history.replaceState({ html: '' }, '', url)

      this.state = 'ready'
    },

    /**
     * Build URL for the query with updated query parameters.
     * @param {Object} options - Options for the function
     * @param {Boolean} loadPrevious - Whether it is a load previous event.
     * @returns {String} formatted URL
     */
    buildQueryParameters({
      loadPrevious,
    }) {
      const url = new URL(window.location.href)

      /**
       * Set page parameter.
       * - Removes the page parameter if the user is on the first page.
       */
      const pageToFetch = loadPrevious ? this.pagination.startPage : this.pagination.page

      if (pageToFetch === 1) {
        url.searchParams.delete('page')
      } else {
        url.searchParams.set('page', pageToFetch)
      }

      /**
       * Set sorting parameter.
       * - Removes the `sort_by` parameter if the user is on the default sorting
       *   order.
       */
      if (this.localSorting.current === this.localSorting.default) {
        url.searchParams.delete('sort_by')
      } else {
        url.searchParams.set('sort_by', this.localSorting.current)
      }

      /**
       * Set filter parameters.
       * - Clears all current filter parameters from the URL.
       * - Appends the new filter params to the URL.
       */
      [...url.searchParams].forEach(([key]) => {
        if (this.filterParamNames.includes(key)) {
          url.searchParams.delete(key)
        }
      })

      if (this.activeFilters.length) {
        this.activeFilters.forEach(activeFilter => {
          if (activeFilter.type === 'price_range') {
            if (activeFilter.minValue.value) {
              url.searchParams.set(
                activeFilter.minValue.paramName,
                activeFilter.minValue.value / 100,
              )
            } else {
              url.searchParams.delete(activeFilter.minValue.paramName)
            }

            if (activeFilter.maxValue.value) {
              url.searchParams.set(
                activeFilter.maxValue.paramName,
                activeFilter.maxValue.value / 100,
              )
            } else {
              url.searchParams.delete(activeFilter.maxValue.paramName)
            }

            return
          }

          const unescapedValue = unenscapeHTMLEntities(activeFilter.value)

          url.searchParams.append(activeFilter.paramName, unescapedValue)
        })
      }

      return url.toString()
    },

    /**
     * Handle saving the current collection data to the query cache.
     * - Only saves if the number of local products is more than the products
     *   per page setting.
     * - If there's 1 page worth of products or less, we can just load that
     *   directly from Liquid and don't need to use the query cache
     * - If we don't save to the query cache, we delete any existing query
     *   cache data to reduce the chance of out of date data.
     * @param {Object} payload - data used to save to the cache
     * @param {Object} collectionData - data from the API response
     * @param {Array} products - Array of products to save
     * @param {String} url - URL to cache
     */
    saveDataToCache({ collectionData, products, url }) {
      if (products.length > this.$variable('settings.pagination.products')) {
        const dataToCache = {
          url,
          data: {
            ...collectionData,
            products,
            pagination: this.pagination,
          },
        }

        queryCache.set(dataToCache)
      } else {
        queryCache.remove()
      }
    },

    /**
     * Fetch collection data with caching.
     * - If cached data exists, return that.
     * - Otherwise, make an API request.
     * @param {String} url - URL to fetch
     */
    getCollectionWithCaching(url) {
      const cachedData = this.getCachedData(url)

      if (cachedData?.data) {
        return cachedData.data
      }

      return this.getRemoteData(url)
    },

    /**
     * Fetch collection data with an API request
     * @param {String} url - URL to fetch
     */
    getRemoteData(url) {
      return queryJSONTemplate(url)
    },

    /**
     * Get data from the cache.
     * - Uses the `getWithValidation` method to validate that the cache data
     *   matches the current state.
     * NOTE: To be expanded on once filter/sort is in place.
     * @param {String} url - current URL
     */
    getCachedData(url) {
      const cachedData = queryCache.getWithValidation((cache) => {
        const isValid = cache.url === url
        return isValid
      })

      return cachedData
    },

    /**
     * Check if the current collection state has cached data and load that on
     * component mounted.
     * - If found, updates the local products and pagination state.
     */
    checkForCachedData() {
      const url = new URL(window.location)

      const cachedData = this.getCachedData(url.href)

      if (!cachedData) {
        return
      }

      this.localProducts = cachedData.data.products
      this.pagination = cachedData.data.pagination
    },

    /**
     * Handle Load More
     * - Increments the page in the pagination object and then fetches data.
     */
    handleLoadMore() {
      if (!this.pagination.hasNextPage) {
        return
      }

      this.pagination.page += 1
      this.queryCollection()
    },

    /**
     * Handle Load Previous
     * - Decrements the start page in the pagination object and then fetches
     *   data.
     */
    handleLoadPrevious() {
      if (!this.pagination.hasPreviousPage) {
        return
      }

      this.pagination.startPage -= 1

      this.queryCollection({
        loadPrevious: true,
      })
    },

    /**
     * Handle filter value changes.
     * - If the filter value is in the `activeFilters` array, then remove it
     * - Otherwise add it to the array.
     * - Then trigger a collection query to fetch new collection data.
     * @param {Object} filterValue - Filter value object
     */
    handleFilterChange(filterValue) {
      const filterValueIndex = this.activeFilters.findIndex((activeFilter) => {
        return activeFilter.paramName === filterValue.paramName &&
          activeFilter.value === filterValue.value
      })

      if (filterValueIndex > -1) {
        this.activeFilters.splice(filterValueIndex, 1)
      } else {
        this.activeFilters.push(filterValue)
      }

      this.queryCollection({
        resetPaging: true,
      })
    },

    handleFilterPriceChange(filterValue) {
      const filterValueIndex = this.activeFilters.findIndex((activeFilter) => {
        return activeFilter.paramName === filterValue.paramName
      })

      const isMinOrMaxSet = Boolean(filterValue.minValue.value || filterValue.maxValue.value)

      /**
       * If a min or max value is set for the price filter, then we add it to
       * the `activeFilters` array.
       * - If a price filter is already in the `activeFilters`, update the entry
       *   with splice, otherwise push the entry to the array.
       * - Otherwise, if neither min or max are set, remove the price filter
       *   from the active filters array.
       */
      if (isMinOrMaxSet) {
        if (filterValueIndex > -1) {
          this.activeFilters.splice(filterValueIndex, 1, filterValue)
        } else {
          this.activeFilters.push(filterValue)
        }

      } else {
        this.activeFilters.splice(filterValueIndex, 1)
      }

      this.queryCollection({
        resetPaging: true,
      })
    },

    /**
     * Handle clearing all filters.
     * - Resets the `activeFilters` array and queries the collection.
     */
    handleClearAll() {
      this.activeFilters = []

      this.queryCollection({
        resetPaging: true,
      })
    },

    /**
     * Handle mobile column change event.
     * - Takes the emitted column count and updates CSS variable and saves the
     *   value to local storage to persist the user's choice.
     * @param {Number} columnCount - selected column count.
     */
    handleMobileColumnCountChange(columnCount) {
      this.mobileColumnCount = columnCount
      document.documentElement.style.setProperty('--mobile-product-grid-columns', columnCount)
      window.localStorage?.setItem('--mobile-product-grid-columns', columnCount)
    },

    /**
     * Handle sort order change.
     * @param {String} sortOrder - new sort order id
     */
    handleSortChange(sortOrder) {
      this.localSorting.current = sortOrder

      this.queryCollection({
        resetPaging: true,
      })
    },

    /**
     * Handle moving focus on to the first new product card when the Load More
     * button is used.
     */
    async updateFocus() {
      await this.$nextTick()

      const gridRef = this.$refs?.productGrid

      if (!gridRef) {
        return
      }

      /**
       * Access the `itemRefs` array from the product grid component instance.
       */
      const itemRefs = gridRef?.itemRefs

      if (!itemRefs) {
        return
      }

      const elementIndex = (this.pagination.page - this.pagination.startPage) * this.$variable('settings.pagination.products')

      const element = itemRefs[elementIndex]

      if (!element) {
        return
      }

      const elementToFocus = element.classList.contains('product-card')
        ? element.querySelector('a')
        : element

      elementToFocus.focus()
    },

    /**
     * Update the local pagination with new page info.
     * @param {Object} pageInfo - Formatted collection page info.
     * @param {Boolean} loadPrevious - Whether request type was a load previous.
     */
    updateLocalPagination(pageInfo, loadPrevious) {
      if (loadPrevious) {
        this.pagination.hasPreviousPage = pageInfo.hasPreviousPage
        return
      }

      this.pagination.hasNextPage = pageInfo.hasNextPage
    },

    /**
     * Set initial pagination object from Liquid props.
     * @param {Object} pageInfo - formatted page information for the collection.
     */
    setInitialPagination(pageInfo) {
      if (!this.collection.pageInfo) {
        return
      }

      const page = pageInfo.currentPage || 1

      this.pagination.page = page
      this.pagination.startPage = page
      this.pagination.hasNextPage = pageInfo.hasNextPage
      this.pagination.hasPreviousPage = pageInfo.hasPreviousPage
    },

    /**
     * Update the component active filters data with initial liquid filter data.
     * @param {Array} filters - Liquid filters data
     */
    setInitialActiveFilters(filters = []) {
      const initialActiveFilters = []

      filters.forEach((filter) => {
        if (filter.type === 'price_range' && (filter.minValue.value || filter.maxValue.value)) {
          initialActiveFilters.push(filter)

          return
        }

        if (filter.activeValues && filter.activeValues.length) {
          initialActiveFilters.push(...filter.activeValues)
        }
      })

      this.activeFilters = initialActiveFilters
    },
  },
}
</script>

<style lang="scss">
@import './main-collection';
</style>
